import React from 'react';
import PropTypes from 'prop-types';
import { Formik, Form } from 'formik';
import moment from 'moment';

import { calculateRenewalInvoiceAmount } from 'common/financeHelpers';

import { formatPrice } from 'common/formatters';
import { INVOICE_SCHEDULE_MONTHLY } from 'common/financeConstants';

import SrBox from 'client/components/SrBox';
import SrFlex from 'client/components/SrFlex';
import SrText from 'client/components/SrText';
import SrModalV2, { MODAL_THEME_FIXED_FOOTER_BUTTON } from 'client/components/SrModalV2/SrModalV2';
import SrFormikField, {
  SR_FIELD_THEME_PARTNER_FORM_ADOBE_GREY,
} from 'client/components/SrFormikField';
import {
  SR_BUTTON_THEME_ADOBE_GREY,
  SR_BUTTON_THEME_ADOBE_BLUE,
} from 'client/components/SrButton/SrButton';

import { createFormValidationFn, validateFormFieldsAndSubmit } from 'client/helpers/formikHelpers';

const getFormFields = ({ markets }) => {
  const marketOptions = [
    ...(markets || []).map((market) => ({
      label: market.displayName,
      value: market.id,
    })),
  ];

  return [
    {
      id: 'tenants',
      title: 'Tenants',
      type: 'short_text',
      properties: {
        placeholder: 'Write tenant names. If multiple write them comma separated',
      },
      validations: ['required'],
    },
    {
      id: 'address',
      title: 'Address',
      type: 'short_text',
      properties: {
        placeholder: 'Write full address',
      },
      validations: ['required'],
    },
    {
      id: 'marketId',
      type: 'select',
      title: 'Market',
      properties: {
        choices: marketOptions,
      },
      validations: ['required'],
    },
    {
      id: 'leaseStartDate',
      title: 'Lease Start Date',
      type: 'date_picker',
      properties: {
        placeholder: 'Choose lease start date',
      },
      validations: ['required'],
    },
    {
      id: 'leaseDuration',
      title: 'Lease Duration (months)',
      type: 'number',
      properties: {
        placeholder: 'Write lease duration (in months)',
        upperLimit: 24,
        lowerLimit: 6,
      },
      validations: ['betweenNumbers', 'required'],
    },
    {
      id: 'rentPrice',
      type: 'currency',
      title: 'Rent Price',
      properties: {
        placeholder: 'Write rent price',
        upperLimit: 10000,
        lowerLimit: 500,
        prefix: '$',
      },
      validations: ['betweenNumbers', 'required'],
    },
    {
      id: 'leaseSignedDate',
      title: 'Lease Signed Date',
      type: 'date_picker',
      properties: {
        placeholder: 'Choose lease signed date',
      },
      validations: ['required'],
    },
  ];
};

const SrBillingRenewalModal = ({ markets, billingFees, onClose, onSubmit, invoiceSchedule }) => {
  const _getSubmitButtonText = ({ formValues }) => {
    const canGenerateButtonText =
      formValues.rentPrice && formValues.leaseStartDate && billingFees.renewalFeePercentage;

    if (!canGenerateButtonText || invoiceSchedule === INVOICE_SCHEDULE_MONTHLY) {
      return 'Submit Renewal';
    }

    const invoiceAmount = formatPrice(
      calculateRenewalInvoiceAmount({
        rentPrice: formValues.rentPrice,
        billingFees,
      }),
    );

    const date = moment(formValues.leaseStartDate).format('MMM Do');

    if (moment().isAfter(moment(formValues.leaseStartDate).startOf('day'))) {
      return `Charge ${invoiceAmount} now`;
    }

    return `Charge ${invoiceAmount} on ${date}`;
  };

  return (
    <Formik
      initialValues={{
        marketId: markets[0]?.id || '',
      }}
      validate={(formValues) => {
        const formFields = getFormFields({ formValues });
        return createFormValidationFn(formFields)(formValues);
      }}
      onSubmit={onSubmit}
    >
      {(formikBag) => {
        const formFields = getFormFields({ markets });
        return (
          <SrModalV2
            theme={MODAL_THEME_FIXED_FOOTER_BUTTON}
            buttonTheme={SR_BUTTON_THEME_ADOBE_GREY}
            contentMaxHeight="700px"
            contentPadding="28px 20px 100px 20px"
            buttonProps={[
              {
                text: 'Close',
                theme: SR_BUTTON_THEME_ADOBE_GREY,
                onClick: onClose,
              },
              {
                text: _getSubmitButtonText({ formValues: formikBag.values }),
                theme: SR_BUTTON_THEME_ADOBE_BLUE,
                onClick: () =>
                  validateFormFieldsAndSubmit({
                    formikBag,
                    formFields,
                  }),
              },
            ]}
          >
            <SrBox>
              <SrFlex marginTop="8px" marginBottom="36px" align="center" justify="center">
                <SrText font="greycliff" color="blueGreycliff" weight="800" fontSize="26px">
                  Charge Renewal
                </SrText>
              </SrFlex>
              <Form className="form">
                {formFields.map((formField) => (
                  <SrBox key={formField.id} marginBottom="16px">
                    <SrFormikField
                      formField={formField}
                      formikBag={formikBag}
                      theme={SR_FIELD_THEME_PARTNER_FORM_ADOBE_GREY}
                      margin={formField.margin}
                    />
                  </SrBox>
                ))}
              </Form>
            </SrBox>
          </SrModalV2>
        );
      }}
    </Formik>
  );
};

SrBillingRenewalModal.propTypes = {
  markets: PropTypes.array.isRequired,
  billingFees: PropTypes.object.isRequired,
  onClose: PropTypes.func.isRequired,
  invoiceSchedule: PropTypes.string.isRequired,
};

SrBillingRenewalModal.displayName = 'SrBillingRenewalModal';

export default SrBillingRenewalModal;
